import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Building2, Loader2, Search, User, UserRound, X } from 'lucide-react';
import { Input } from '@/components/Input';
import { formatMetaInsightsValue } from '@/utils/currency';
import { useLabels, useWorkspace } from '@/contexts/WorkspaceContext';
import { useModuleAccess } from '@/contexts/ModuleAccessContext';
import { contactsService } from '@/services/contacts.service';
import { leadsService } from '@/services/leads.service';
import { settingsService, TeamUser } from '@/services/settings.service';
import {
  AreaUnit, Furnishing, LeasePhase, LeaseStatus, PropertyType, PropertyUnit, RentPeriod, UnitPurpose, UnitStatus, UnitType,
  ViewingInterest, ViewingStatus, realEstateService,
} from '@/services/real-estate.service';

type Variant = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

// ── Words ──────────────────────────────────────────────────────────────────

export const PROPERTY_TYPE_LABEL: Record<PropertyType, string> = {
  building: 'Building', tower: 'Tower', compound: 'Compound', villa: 'Villa', townhouse: 'Townhouse',
  office_building: 'Office building', retail: 'Retail', warehouse: 'Warehouse', land: 'Land', other: 'Other',
};
export const UNIT_TYPE_LABEL: Record<UnitType, string> = {
  apartment: 'Apartment', studio: 'Studio', penthouse: 'Penthouse', villa: 'Villa', townhouse: 'Townhouse', office: 'Office',
  shop: 'Shop', warehouse: 'Warehouse', plot: 'Plot', room: 'Room', other: 'Other',
};
export const PURPOSE_LABEL: Record<UnitPurpose, string> = { rent: 'For rent', sale: 'For sale', both: 'Rent or sale' };
export const UNIT_STATUS_LABEL: Record<UnitStatus, string> = {
  available: 'Available', reserved: 'Reserved', rented: 'Rented', sold: 'Sold', off_market: 'Off market',
};
export const UNIT_STATUS_VARIANT: Record<UnitStatus, Variant> = {
  available: 'success', reserved: 'warning', rented: 'info', sold: 'neutral', off_market: 'neutral',
};
export const FURNISHING_LABEL: Record<Furnishing, string> = { furnished: 'Furnished', semi_furnished: 'Semi-furnished', unfurnished: 'Unfurnished' };
export const AREA_UNIT_LABEL: Record<AreaUnit, string> = { sqft: 'sq ft', sqm: 'sq m', marla: 'marla', kanal: 'kanal', sqyd: 'sq yd' };
export const RENT_PERIOD_LABEL: Record<RentPeriod, string> = { yearly: 'per year', monthly: 'per month' };
export const VIEWING_STATUS_LABEL: Record<ViewingStatus, string> = { scheduled: 'Scheduled', completed: 'Completed', cancelled: 'Cancelled', no_show: 'No-show' };
export const VIEWING_STATUS_VARIANT: Record<ViewingStatus, Variant> = { scheduled: 'info', completed: 'success', cancelled: 'neutral', no_show: 'danger' };
export const INTEREST_LABEL: Record<ViewingInterest, string> = { hot: 'Hot', warm: 'Warm', cold: 'Cold' };
export const INTEREST_CLASS: Record<ViewingInterest, string> = {
  hot: 'bg-danger-soft text-danger', warm: 'bg-warning-soft text-warning', cold: 'bg-info-soft text-info',
};
export const LEASE_STATUS_LABEL: Record<LeaseStatus, string> = { active: 'Active', ended: 'Ended', terminated: 'Terminated' };
export const LEASE_PHASE_LABEL: Record<LeasePhase, string> = {
  upcoming: 'Starts soon', current: 'Active', expiring: 'Ending soon', past_end: 'Past end date', ended: 'Ended', terminated: 'Terminated',
};
export const LEASE_PHASE_VARIANT: Record<LeasePhase, Variant> = {
  upcoming: 'info', current: 'success', expiring: 'warning', past_end: 'danger', ended: 'neutral', terminated: 'neutral',
};

// ── Money & specs ──────────────────────────────────────────────────────────

/** Major-unit amount ("AED 85,000"); whole amounts drop the ".00" property prices never use. */
export function formatPrice(amount: number | null | undefined, currency: string): string {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) return '—';
  const s = formatMetaInsightsValue(String(amount), currency);
  return Number.isInteger(amount) ? s.replace(/\.00$/, '') : s;
}

export const periodShort = (p?: RentPeriod) => (p === 'monthly' ? '/mo' : '/yr');

export function bedsLabel(b?: number | null): string | null {
  if (b === null || b === undefined) return null;
  return b === 0 ? 'Studio' : `${b} bed`;
}

type SpecUnit = Pick<PropertyUnit, 'unitType' | 'bedrooms'> & Partial<Pick<PropertyUnit, 'bathrooms' | 'area' | 'areaUnit' | 'floor'>>;

/** "Apartment · 2 bed · 2 bath · 1,250 sq ft". */
export function unitSpecs(u: SpecUnit, withFloor = false): string {
  const bits: string[] = [UNIT_TYPE_LABEL[u.unitType] ?? u.unitType];
  const beds = bedsLabel(u.bedrooms);
  if (beds && !(u.unitType === 'studio' && u.bedrooms === 0)) bits.push(beds);
  if (u.bathrooms !== null && u.bathrooms !== undefined) bits.push(`${u.bathrooms} bath`);
  if (u.area !== null && u.area !== undefined) bits.push(`${u.area.toLocaleString()} ${AREA_UNIT_LABEL[u.areaUnit as AreaUnit] ?? u.areaUnit ?? ''}`.trim());
  if (withFloor && u.floor !== null && u.floor !== undefined) bits.push(u.floor === 0 ? 'Ground floor' : `Floor ${u.floor}`);
  return bits.join(' · ');
}

type PriceUnit = Pick<PropertyUnit, 'purpose' | 'rentPeriod' | 'currency'> & { rentPrice?: number | null; salePrice?: number | null };

/** Rent and / or sale price lines for the unit's purpose. */
export function unitPrices(u: PriceUnit, fallbackCurrency: string): Array<{ kind: 'rent' | 'sale'; text: string; missing: boolean }> {
  const cur = u.currency || fallbackCurrency;
  const out: Array<{ kind: 'rent' | 'sale'; text: string; missing: boolean }> = [];
  if (u.purpose !== 'sale') {
    const has = u.rentPrice !== null && u.rentPrice !== undefined;
    out.push({ kind: 'rent', text: has ? `${formatPrice(u.rentPrice, cur)}${periodShort(u.rentPeriod)}` : 'Rent not set', missing: !has });
  }
  if (u.purpose !== 'rent') {
    const has = u.salePrice !== null && u.salePrice !== undefined;
    out.push({ kind: 'sale', text: has ? formatPrice(u.salePrice, cur) : 'Price not set', missing: !has });
  }
  return out;
}

export const placeLine = (p?: { community?: string; city?: string } | null) => [p?.community, p?.city].filter(Boolean).join(', ');

// ── Dates & the company clock ──────────────────────────────────────────────

const pad2 = (n: number) => String(n).padStart(2, '0');

export function browserTimezone(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
}

/** Viewings are booked on the company's clock, not the viewer's browser. */
export function useCompanyTimezone(): string {
  const { info } = useWorkspace();
  const tz = info?.timezone || browserTimezone();
  try {
    Intl.DateTimeFormat('en-US', { timeZone: tz }).format(0);
    return tz;
  } catch {
    return 'UTC';
  }
}

function zoneParts(ms: number, tz: string) {
  const parts = Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(ms));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return { y: get('year'), mo: get('month'), d: get('day'), h: get('hour') % 24, mi: get('minute'), s: get('second') };
}

function offsetMs(ms: number, tz: string): number {
  const p = zoneParts(ms, tz);
  return Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s) - Math.floor(ms / 1000) * 1000;
}

/** "2026-10-02" + "14:30" on `tz`'s clock → UTC ISO instant. */
export function zonedToIso(ymd: string, hm: string, tz: string): string {
  const [y, mo, d] = ymd.split('-').map(Number);
  const [h, mi] = hm.split(':').map(Number);
  const wall = Date.UTC(y, mo - 1, d, h, mi);
  let ms = wall - offsetMs(wall, tz);
  // Second pass lands DST-edge times on the offset that actually applies then.
  const again = wall - offsetMs(ms, tz);
  if (again !== ms) ms = again;
  return new Date(ms).toISOString();
}

/** A UTC instant as date + time on `tz`'s clock. */
export function isoToZoned(iso: string, tz: string): { ymd: string; hm: string } {
  const p = zoneParts(new Date(iso).getTime(), tz);
  return { ymd: `${p.y}-${pad2(p.mo)}-${pad2(p.d)}`, hm: `${pad2(p.h)}:${pad2(p.mi)}` };
}

export function formatZoned(iso: string, tz: string, opts: Intl.DateTimeFormatOptions): string {
  try {
    return new Intl.DateTimeFormat(undefined, { ...opts, timeZone: tz }).format(new Date(iso));
  } catch {
    return new Date(iso).toLocaleString();
  }
}
export const timeInZone = (iso: string, tz: string) => formatZoned(iso, tz, { hour: '2-digit', minute: '2-digit' });
export const dayInZone = (iso: string, tz: string) => formatZoned(iso, tz, { weekday: 'short', day: 'numeric', month: 'short' });

/** Calendar dates (lease terms, due dates, available-from) are stored as UTC midnight. */
export function formatDay(iso?: string | null, opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { ...opts, timeZone: 'UTC' });
}
export const ymdOf = (iso?: string | null) => (iso ? iso.slice(0, 10) : '');

export function addDaysYmd(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}
export function addYearsYmd(ymd: string, years: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y + years, m - 1, d)).toISOString().slice(0, 10);
}

// ── Access & people ────────────────────────────────────────────────────────

/** What the rest of the CRM lets these pages link to or search. */
export function useRealEstateAccess() {
  const { pageOn } = useWorkspace();
  const { has } = useModuleAccess();
  return {
    contactSearch: has('contacts'),
    contactLinks: has('contacts') && pageOn('contacts'),
    leadSearch: has('leads') || has('whatsapp_inbox'),
    leadLinks: has('leads') && pageOn('leads'),
    viewings: has('viewings') && pageOn('viewings'),
    leases: has('leases') && pageOn('leases'),
  };
}

/** "an enquiry", "a lead" — the words come from the company's blueprint. */
export const withArticle = (word: string) => `${/^[aeiou]/i.test(word) ? 'an' : 'a'} ${word}`;

export const personName = (u: { firstName?: string; lastName?: string; email?: string }) =>
  [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || 'Unnamed';

/** Active team members — the backend refuses anyone else as a viewing agent. */
export function useTeam(): TeamUser[] {
  const [team, setTeam] = useState<TeamUser[]>([]);
  useEffect(() => {
    let live = true;
    settingsService.listTeam()
      .then((r) => { if (live) setTeam(r.filter((u) => u.status === 'active')); })
      .catch(() => { /* interceptor toasts; the picker stays empty */ });
    return () => { live = false; };
  }, []);
  return team;
}

/** The backend's own words for a refusal (string, or class-validator's list). */
export function apiMessage(e: unknown): string | null {
  const m = (e as { response?: { data?: { message?: unknown } } } | null)?.response?.data?.message;
  if (typeof m === 'string') return m;
  if (Array.isArray(m)) return m.filter((x) => typeof x === 'string').join(' ') || null;
  return null;
}

// ── UI bits ────────────────────────────────────────────────────────────────

const TONES = {
  gold: 'bg-primary-soft text-primary',
  green: 'bg-success-soft text-success',
  blue: 'bg-info-soft text-info',
  amber: 'bg-warning-soft text-warning',
  red: 'bg-danger-soft text-danger',
} as const;

/** Summary tile; `value` undefined shows a skeleton, `onClick` makes it a quick filter. */
export function Kpi({ label, value, icon, tone = 'gold', hint, onClick, active }: {
  label: string;
  value?: string | number;
  icon: React.ReactNode;
  tone?: keyof typeof TONES;
  hint?: string;
  onClick?: () => void;
  active?: boolean;
}) {
  const long = typeof value === 'string' && value.length > 9;
  const body = (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-2xs font-semibold uppercase tracking-[0.12em] text-text-muted">{label}</span>
        <span className={clsx('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', TONES[tone])}>{icon}</span>
      </div>
      {value === undefined ? (
        <div className="skeleton mt-2 h-7 w-14 rounded" />
      ) : (
        <div className={clsx('mt-1 truncate font-display font-bold leading-tight tracking-tight tabular-nums text-text-primary', long ? 'text-xl' : 'text-[26px]')}>{value}</div>
      )}
      <div className="mt-0.5 h-4 truncate text-2xs text-text-muted">{hint ?? ''}</div>
    </>
  );
  const cls = clsx(
    'min-w-0 rounded-2xl border bg-surface p-4 text-left shadow-soft',
    active ? 'border-primary/60 ring-2 ring-primary-soft' : 'border-border',
  );
  return onClick ? (
    <button type="button" onClick={onClick} aria-pressed={active} className={clsx(cls, 'transition-colors hover:border-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary')}>
      {body}
    </button>
  ) : (
    <div className={cls}>{body}</div>
  );
}

export const Pill: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => (
  <span className={clsx('inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-2xs font-semibold', className ?? 'bg-surface-alt text-text-secondary')}>
    {children}
  </span>
);

/** A picked contact / lead / unit, with a way to clear it. */
export function PickedChip({ title, subtitle, icon, onClear, disabled }: {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  onClear?: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-xl border border-primary/30 bg-primary-soft/50 px-3 py-2">
      <div className="flex min-w-0 items-center gap-2.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-on-primary">
          {icon ?? <span className="text-xs font-bold">{title.slice(0, 1).toUpperCase()}</span>}
        </span>
        <div className="min-w-0">
          <div className="truncate text-[13px] font-semibold text-text-primary">{title}</div>
          {subtitle && <div className="truncate text-2xs text-text-muted">{subtitle}</div>}
        </div>
      </div>
      {onClear && !disabled && (
        <button type="button" onClick={onClear} aria-label={`Remove ${title}`} className="rounded-lg p-1 text-text-muted hover:bg-surface hover:text-text-primary">
          <X size={14} />
        </button>
      )}
    </div>
  );
}

interface SearchPickerProps<T> {
  placeholder: string;
  search: (q: string) => Promise<T[]>;
  keyOf: (item: T) => string;
  renderItem: (item: T) => React.ReactNode;
  onPick: (item: T) => void;
  icon?: React.ReactNode;
  emptyText?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  /** With an empty box, list the first page (recent records) instead of waiting for a query. */
  browse?: boolean;
  ariaLabel?: string;
}

/** Search box whose results open inline under it — no popover to be clipped by a scrolling modal. */
function SearchPicker<T>({ placeholder, search, keyOf, renderItem, onPick, icon, emptyText = 'No matches', disabled, autoFocus, browse, ariaLabel }: SearchPickerProps<T>) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [active, setActive] = useState(0);
  const searchRef = useRef(search);
  const reqId = useRef(0);
  useEffect(() => { searchRef.current = search; }, [search]);

  const term = q.trim();
  const ready = open && (term.length >= 2 || (browse && term.length === 0));

  useEffect(() => {
    if (!ready) {
      reqId.current++;
      setItems([]);
      setDone(false);
      setLoading(false);
      return;
    }
    const id = ++reqId.current;
    setLoading(true);
    const h = setTimeout(async () => {
      try {
        const r = await searchRef.current(term);
        if (id === reqId.current) { setItems(r); setActive(0); }
      } catch {
        if (id === reqId.current) setItems([]);
      } finally {
        if (id === reqId.current) { setLoading(false); setDone(true); }
      }
    }, term ? 250 : 0);
    return () => clearTimeout(h);
  }, [ready, term]);

  const pick = (item: T) => {
    onPick(item);
    setQ('');
    setOpen(false);
  };

  return (
    <div>
      <Input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (!items.length) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(items.length - 1, a + 1)); }
          if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
          if (e.key === 'Enter') { e.preventDefault(); pick(items[Math.min(active, items.length - 1)]); }
        }}
        placeholder={placeholder}
        iconLeft={icon ?? <Search size={15} />}
        iconRight={loading ? <Loader2 size={14} className="animate-spin" /> : undefined}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-label={ariaLabel ?? placeholder}
      />
      {ready && (items.length > 0 || done) && (
        <ul className="mt-1 max-h-64 overflow-y-auto rounded-xl border border-border bg-surface shadow-soft" role="listbox">
          {items.length === 0 ? (
            <li className="px-3 py-2.5 text-xs text-text-muted">{loading ? 'Searching…' : emptyText}</li>
          ) : items.map((item, i) => (
            <li key={keyOf(item)} role="option" aria-selected={i === active}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(item)}
                className={clsx('flex w-full items-center justify-between gap-3 px-3 py-2 text-left', i === active ? 'bg-surface-alt' : 'hover:bg-surface-alt')}
              >
                {renderItem(item)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export interface PickedContact { contactUid: string; name: string; phone?: string }

const searchContacts = async (q: string): Promise<PickedContact[]> => {
  const r = await contactsService.list({ q: q || undefined, limit: 8 });
  return r.items.map((c) => ({ contactUid: c.contactUid, name: [c.firstName, c.lastName].filter(Boolean).join(' ') || c.phone, phone: c.phone }));
};

/** Contact search + picked chip. Shows a note instead when contacts are off for the company. */
export function ContactPicker({ value, onChange, placeholder, disabled, autoFocus }: {
  value: PickedContact | null;
  onChange: (c: PickedContact | null) => void;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  const t = useLabels();
  const { contactSearch } = useRealEstateAccess();
  const word = t('contacts', 'singular').toLowerCase();
  if (value) {
    return <PickedChip title={value.name} subtitle={value.phone} icon={<User size={15} />} onClear={() => onChange(null)} disabled={disabled} />;
  }
  if (!contactSearch) {
    return <p className="rounded-lg border border-dashed border-border px-3 py-2.5 text-xs text-text-muted">{t('contacts')} aren’t enabled for your workspace.</p>;
  }
  return (
    <SearchPicker<PickedContact>
      placeholder={placeholder ?? `Find ${withArticle(word)} by name or phone`}
      search={searchContacts}
      keyOf={(c) => c.contactUid}
      onPick={onChange}
      icon={<User size={15} />}
      emptyText={`No ${t('contacts').toLowerCase()} match`}
      disabled={disabled}
      autoFocus={autoFocus}
      browse
      renderItem={(c) => (
        <>
          <span className="truncate text-[13px] font-medium text-text-primary">{c.name}</span>
          {c.phone && <span className="shrink-0 text-2xs tabular-nums text-text-muted">{c.phone}</span>}
        </>
      )}
    />
  );
}

export interface PickedLead { leadUid: string; name: string; phone?: string; status?: string }

const searchLeads = async (q: string): Promise<PickedLead[]> => {
  const r = await leadsService.list({ q: q || undefined, limit: 8 });
  return r.items.map((l) => ({ leadUid: l.leadUid, name: l.profileName || l.phone || 'Unnamed', phone: l.phone, status: l.status }));
};

export function LeadPicker({ value, onChange, disabled, autoFocus }: {
  value: PickedLead | null;
  onChange: (l: PickedLead | null) => void;
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  const t = useLabels();
  const word = t('leads', 'singular').toLowerCase();
  if (value) {
    return <PickedChip title={value.name} subtitle={[value.phone, value.status].filter(Boolean).join(' · ')} icon={<UserRound size={15} />} onClear={() => onChange(null)} disabled={disabled} />;
  }
  return (
    <SearchPicker<PickedLead>
      placeholder={`Find ${withArticle(word)} by name or phone`}
      search={searchLeads}
      keyOf={(l) => l.leadUid}
      onPick={onChange}
      icon={<UserRound size={15} />}
      emptyText={`No ${t('leads').toLowerCase()} match`}
      disabled={disabled}
      autoFocus={autoFocus}
      browse
      renderItem={(l) => (
        <>
          <span className="min-w-0 truncate text-[13px] font-medium text-text-primary">{l.name}</span>
          <span className="shrink-0 text-2xs tabular-nums text-text-muted">{[l.phone, l.status].filter(Boolean).join(' · ')}</span>
        </>
      )}
    />
  );
}

/** Unit search. `purpose` narrows server-side; `accept` drops units the action can't use. */
export function UnitPicker({ value, onChange, purpose, accept, disabled, autoFocus, fallbackCurrency }: {
  value: PropertyUnit | null;
  onChange: (u: PropertyUnit | null) => void;
  purpose?: 'rent' | 'sale';
  accept?: (u: PropertyUnit) => boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  fallbackCurrency: string;
}) {
  const acceptRef = useRef(accept);
  useEffect(() => { acceptRef.current = accept; }, [accept]);
  const search = useCallback(async (q: string) => {
    const r = await realEstateService.listUnits({ q: q || undefined, purpose, limit: 25 });
    return r.items.filter((u) => (acceptRef.current ? acceptRef.current(u) : true)).slice(0, 10);
  }, [purpose]);
  if (value) {
    const price = unitPrices(value, fallbackCurrency)[0];
    return (
      <PickedChip
        title={`${value.label}${value.property ? ` · ${value.property.name}` : ''}`}
        subtitle={[unitSpecs(value), price && !price.missing ? price.text : null].filter(Boolean).join(' · ')}
        icon={<Building2 size={15} />}
        onClear={() => onChange(null)}
        disabled={disabled}
      />
    );
  }
  return (
    <SearchPicker<PropertyUnit>
      placeholder="Find a unit by label, reference or property"
      search={search}
      keyOf={(u) => u.unitUid}
      onPick={onChange}
      icon={<Building2 size={15} />}
      emptyText="No units that fit"
      disabled={disabled}
      autoFocus={autoFocus}
      browse
      renderItem={(u) => (
        <>
          <span className="min-w-0">
            <span className="block truncate text-[13px] font-medium text-text-primary">
              {u.label}{u.property ? <span className="font-normal text-text-muted"> · {u.property.name}</span> : null}
            </span>
            <span className="block truncate text-2xs text-text-muted">{unitSpecs(u)}{placeLine(u.property) ? ` · ${placeLine(u.property)}` : ''}</span>
          </span>
          <span className="shrink-0 text-right text-2xs font-medium text-text-secondary">
            {UNIT_STATUS_LABEL[u.status]}
          </span>
        </>
      )}
    />
  );
}
